import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, KeyRound } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Modal } from "components/ui/modal";
import { Switch } from "components/ui/switch";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n, LANGUAGE_OPTIONS } from "i18n";
import api from "lib/api";

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2";

/**
 * Modal de credenciais — exibido uma unica vez, no cadastro e no reset do MFA.
 * O canal oficial continua sendo o e-mail (ver services/mailer.py:send_welcome);
 * a tela so repete os dados para o admin repassar caso o e-mail nao chegue.
 */
function CredentialsModal({ open, credentials, title, description, onClose }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const text = credentials?.password
      ? `${credentials.email} / ${credentials.password}`
      : credentials?.mfaSecret || credentials?.email || "";
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="md"
      icon={
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        </div>
      }
      footer={
        <>
          <Button variant="outline" onClick={copy}>
            <Copy className="h-4 w-4 mr-2" />
            {copied ? t("common.copied") : t("common.copy")}
          </Button>
          <Button onClick={onClose}>{t("common.done")}</Button>
        </>
      }
    >
      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2 text-left">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">{t("common.email")}</span>
          <span className="font-medium break-all">{credentials?.email}</span>
        </div>
        {credentials?.password && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{t("account.initialPassword")}</span>
            <span className="font-mono font-medium break-all">{credentials.password}</span>
          </div>
        )}
      </div>

      {credentials?.mfaQr && (
        <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4 text-left">
          <p className="text-sm font-medium mb-1">{t("account.mfaTitle")}</p>
          <p className="text-xs text-muted-foreground mb-3">{t("account.mfaHint")}</p>
          <div className="flex flex-col items-center gap-3">
            <img
              src={credentials.mfaQr}
              alt={t("account.mfaTitle")}
              className="h-40 w-40 rounded bg-white p-2"
            />
            {credentials.mfaSecret && (
              <code className="text-xs break-all text-center text-muted-foreground">
                {credentials.mfaSecret}
              </code>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Cadastro/edicao de usuario com os tres niveis de permissao.
 * Espelha ../sec_face/frontend/src/pages/admin/AccountForm.js.
 */
export default function AccountForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const { t } = useI18n();
  const isEditing = Boolean(id);

  const canManage = !!user?.capabilities?.manage_users;
  // Conceder o nivel admin exige ser admin (a API valida de novo).
  const canGrantAdmin = !!user?.capabilities?.manage_system;

  const [role, setRole] = useState("user");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [language, setLanguage] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [cannotChange, setCannotChange] = useState(false);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [mfaReset, setMfaReset] = useState(null);

  useEffect(() => {
    if (!isEditing) return;
    api
      .get(`/api/accounts/${id}/`)
      .then((res) => {
        const a = res.data;
        setEmail(a.email || "");
        setRole(a.role || "user");
        setFirstName(a.first_name || "");
        setLastName(a.last_name || "");
        setMobilePhone(a.mobile_phone || "");
        setLanguage(a.language || "");
        setCountry(a.country || "");
        setState(a.state || "");
        setCity(a.city || "");
        setMustChange(!!a.must_change_pass);
        setCannotChange(!!a.cannot_change_pass);
      })
      .catch(() => setError(t("common.loadError")))
      .finally(() => setFetching(false));
  }, [id, isEditing, t]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  // Obrigar a troca e proibir a troca sao politicas opostas (o backend recusa
  // a combinacao; aqui a UI ja impede o estado invalido).
  const toggleMustChange = (value) => {
    setMustChange(value);
    if (value) setCannotChange(false);
  };
  const toggleCannotChange = (value) => {
    setCannotChange(value);
    if (value) setMustChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const payload = {
      email,
      role,
      first_name: firstName,
      last_name: lastName,
      mobile_phone: mobilePhone,
      language,
      country,
      state,
      city,
      must_change_pass: mustChange,
      cannot_change_pass: cannotChange,
    };

    try {
      if (isEditing) {
        await api.put(`/api/accounts/${id}/`, payload);
        navigate("/app/admin/accounts");
      } else {
        const res = await api.post("/api/accounts/", payload);
        // A senha e o QR do MFA seguem por e-mail; a tela repete os dados uma vez.
        setCreated({
          email: res.data.email,
          password: res.data.initial_password,
          mfaSecret: res.data.mfa_secret,
          mfaQr: res.data.mfa_qr,
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("common.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetMfa = async () => {
    let failed = false;
    let payload = null;

    const confirmed = await confirm({
      title: t("account.resetMfaTitle"),
      description: t("account.resetMfaBody"),
      variant: "warning",
      confirmLabel: t("account.resetMfa"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          const res = await api.post(`/api/accounts/${id}/reset-mfa/`);
          payload = res.data;
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed && payload) {
      setMfaReset({
        email,
        mfaSecret: payload.mfa_secret,
        mfaQr: payload.mfa_qr,
      });
      return;
    }
    if (failed) {
      await alert({
        title: t("account.resetMfaError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate("/app/admin/accounts")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <h1 className="text-2xl font-bold tracking-tight mb-1">
        {isEditing ? t("account.edit") : t("account.new")}
      </h1>
      <p className="text-sm text-muted-foreground mb-6">{t("account.newSubtitle")}</p>

      <Card className="w-full">
        <CardContent className="pt-6">
          {fetching ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="w-full space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="acc-email">{t("common.email")}</Label>
                  <Input
                    id="acc-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-role">{t("account.role")}</Label>
                  <select
                    id="acc-role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="user">{t("account.role.user")}</option>
                    <option value="operator">{t("account.role.operator")}</option>
                    {/* So um admin concede o nivel admin (checado tambem na API) */}
                    {canGrantAdmin && (
                      <option value="admin">{t("account.role.admin")}</option>
                    )}
                  </select>
                  <p className="text-xs text-muted-foreground">{t("account.roleHint")}</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="acc-first">{t("common.firstName")}</Label>
                  <Input
                    id="acc-first"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-last">{t("common.lastName")}</Label>
                  <Input
                    id="acc-last"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="acc-phone">{t("common.phone")}</Label>
                  <Input
                    id="acc-phone"
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-language">{t("account.language")}</Label>
                  <select
                    id="acc-language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="">{t("account.languageDefault")}</option>
                    {LANGUAGE_OPTIONS.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="acc-country">{t("common.country")}</Label>
                  <Input
                    id="acc-country"
                    value={country}
                    onChange={(e) =>
                      setCountry(
                        e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase()
                      )
                    }
                    placeholder={t("common.countryPlaceholder")}
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-state">{t("common.state")}</Label>
                  <Input
                    id="acc-state"
                    value={state}
                    onChange={(e) => setState(e.target.value.slice(0, 100))}
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc-city">{t("common.city")}</Label>
                  <Input
                    id="acc-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value.slice(0, 100))}
                    maxLength={100}
                  />
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Label>{t("account.policyLabel")}</Label>
                <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                  <Switch
                    checked={mustChange}
                    onChange={toggleMustChange}
                    ariaLabel={t("account.mustChange")}
                  />
                  <span
                    className="text-sm cursor-pointer select-none"
                    onClick={() => toggleMustChange(!mustChange)}
                  >
                    {t("account.mustChange")}
                  </span>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                  <Switch
                    checked={cannotChange}
                    onChange={toggleCannotChange}
                    ariaLabel={t("account.cannotChange")}
                  />
                  <span
                    className="text-sm cursor-pointer select-none"
                    onClick={() => toggleCannotChange(!cannotChange)}
                  >
                    {t("account.cannotChange")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{t("account.policyExclusive")}</p>
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <div className="flex justify-between gap-3 pt-2">
                {isEditing ? (
                  <Button type="button" variant="outline" onClick={handleResetMfa}>
                    <KeyRound className="h-4 w-4 mr-2" />
                    {t("account.resetMfa")}
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/app/admin/accounts")}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" loading={loading}>
                    {isEditing ? t("common.save") : t("common.create")}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Confirmacao de cadastro — modal proprio do sistema, nunca do navegador */}
      <CredentialsModal
        open={!!created}
        credentials={created}
        title={t("account.registered")}
        description={t("account.credentialsSent")}
        onClose={() => navigate("/app/admin/accounts")}
      />

      <CredentialsModal
        open={!!mfaReset}
        credentials={mfaReset}
        title={t("account.resetMfaDone")}
        description={t("account.resetMfaDoneBody")}
        onClose={() => setMfaReset(null)}
      />
    </div>
  );
}
